'use client';

import { useState, useEffect } from 'react';
import type { ArchiMateElement } from '@/lib/archimate/types';
import { getAllDiagrams } from '@/lib/archimate/storage/db';
import { getElementMetadata } from '@/lib/archimate/elements';
import { PaletteIcon } from './PaletteIcon';
import { ContextMenu } from './ContextMenu';
import { DiagramContextMenu } from './DiagramContextMenu';
import { RenameDiagramDialog } from './RenameDiagramDialog';

interface DiagramMetadata {
  id: string;
  name: string;
  elementCount: number;
  relationshipCount: number;
  updatedAt: number;
}

interface ComponentLibraryProps {
  onSelectComponent: (element: ArchiMateElement) => void;
  onDragComponent?: (element: ArchiMateElement) => void;
  onClose: () => void;
  docked?: boolean;
  onToggleDock?: () => void;
  onRenameComponent?: (element: ArchiMateElement, newName: string) => void;
  onDeleteComponent?: (element: ArchiMateElement) => void;
  onInspectComponent?: (element: ArchiMateElement) => void;
  onLoadDiagram?: (diagramId: string) => void;
  onRenameDiagram?: (diagramId: string, newName: string) => void;
  onDeleteDiagram?: (diagramId: string) => void;
}

export function ComponentLibrary({
  onSelectComponent,
  onDragComponent,
  onClose,
  docked = false,
  onToggleDock,
  onRenameComponent,
  onDeleteComponent,
  onInspectComponent,
  onLoadDiagram,
  onRenameDiagram,
  onDeleteDiagram,
}: ComponentLibraryProps) {
  const [allComponents, setAllComponents] = useState<ArchiMateElement[]>([]);
  const [allDiagrams, setAllDiagrams] = useState<DiagramMetadata[]>([]);
  const [filter, setFilter] = useState('');
  const [groupBy, setGroupBy] = useState<'type' | 'layer'>('layer');
  const [activeTab, setActiveTab] = useState<'components' | 'diagrams'>('components');
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    element: ArchiMateElement;
  } | null>(null);
  const [diagramContextMenu, setDiagramContextMenu] = useState<{
    x: number;
    y: number;
    diagram: DiagramMetadata;
  } | null>(null);
  const [renameElement, setRenameElement] = useState<ArchiMateElement | null>(null);
  const [renameName, setRenameName] = useState('');
  const [renameDiagram, setRenameDiagram] = useState<DiagramMetadata | null>(null);
  const [renameDiagramName, setRenameDiagramName] = useState('');

  useEffect(() => {
    loadAllComponents();

    // Refresh every 2 seconds if docked (to show latest changes)
    if (docked) {
      const interval = setInterval(loadAllComponents, 2000);
      return () => clearInterval(interval);
    }
  }, [docked]);

  const loadAllComponents = async () => {
    const diagrams = await getAllDiagrams();
    const allElements: ArchiMateElement[] = [];

    // Store diagram metadata
    setAllDiagrams(diagrams);

    // Extract all elements from all diagrams
    for (const diagramMeta of diagrams) {
      const fullDiagram = await import('@/lib/archimate/storage/db').then(m => m.loadDiagram(diagramMeta.id));
      if (fullDiagram) {
        allElements.push(...fullDiagram.elements);
      }
    }

    // Deduplicate by masterId - show each unique component once
    const uniqueComponents = new Map<string, ArchiMateElement>();

    for (const element of allElements) {
      const masterKey = element.masterId || element.id;

      // Keep the first occurrence (or the master if we find it)
      if (!uniqueComponents.has(masterKey) || element.isMaster) {
        uniqueComponents.set(masterKey, element);
      }
    }

    setAllComponents(Array.from(uniqueComponents.values()));
  };

  // Filter components
  const filteredComponents = allComponents.filter(c =>
    c.name.toLowerCase().includes(filter.toLowerCase()) ||
    c.type.includes(filter.toLowerCase())
  );

  // Group components
  const grouped = filteredComponents.reduce((acc, comp) => {
    const metadata = getElementMetadata(comp.type);
    if (!metadata) return acc;

    const key = groupBy === 'layer' ? metadata.layer : comp.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(comp);
    return acc;
  }, {} as Record<string, ArchiMateElement[]>);

  // Alphabetize components within each group
  Object.keys(grouped).forEach(key => {
    grouped[key].sort((a, b) => a.name.localeCompare(b.name));
  });

  // Docked mode: sidebar on left
  if (docked) {
    return (
      <div className="w-80 border-r border-border bg-surface flex flex-col h-full">
        {/* Header */}
        <div className="border-b border-border p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-foreground">Library</h3>
            <div className="flex gap-2">
              {onToggleDock && (
                <button
                  onClick={onToggleDock}
                  className="text-foreground-tertiary hover:text-foreground text-sm"
                  title="Undock"
                >
                  ⇱
                </button>
              )}
              <button
                onClick={onClose}
                className="text-foreground-tertiary hover:text-foreground"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-3">
            <button
              onClick={() => setActiveTab('components')}
              className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                activeTab === 'components'
                  ? 'bg-accent text-white'
                  : 'bg-background text-foreground-secondary hover:bg-background/80'
              }`}
            >
              Components
            </button>
            <button
              onClick={() => setActiveTab('diagrams')}
              className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                activeTab === 'diagrams'
                  ? 'bg-accent text-white'
                  : 'bg-background text-foreground-secondary hover:bg-background/80'
              }`}
            >
              Diagrams
            </button>
          </div>

          {activeTab === 'components' && (
            <>
              {/* Search */}
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1 bg-background border border-border rounded text-foreground text-sm"
              />

              {/* Group by */}
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as 'type' | 'layer')}
                className="w-full mt-2 px-2 py-1 bg-background border border-border rounded text-foreground text-sm"
              >
                <option value="layer">By Layer</option>
                <option value="type">By Type</option>
              </select>

              <div className="mt-2 text-xs text-foreground-muted">
                {filteredComponents.length} components
              </div>
            </>
          )}

          {activeTab === 'diagrams' && (
            <div className="mt-2 text-xs text-foreground-muted">
              {allDiagrams.length} diagrams
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-3">
          {activeTab === 'components' && (
            <>
              {Object.entries(grouped)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([group, components]) => (
                  <div key={group} className="mb-4">
                    <h4 className="text-xs font-semibold text-accent mb-2 uppercase">
                      {group} ({components.length})
                    </h4>
                    <div className="space-y-1">
                      {components.map((comp) => {
                        const metadata = getElementMetadata(comp.type);
                        if (!metadata) return null;

                        return (
                          <button
                            key={comp.id}
                            onClick={() => onSelectComponent(comp)}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setContextMenu({
                                x: e.clientX,
                                y: e.clientY,
                                element: comp,
                              });
                            }}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('component', JSON.stringify(comp));
                              e.dataTransfer.effectAllowed = 'copy';
                              if (onDragComponent) {
                                onDragComponent(comp);
                              }
                            }}
                            className="w-full flex items-center gap-2 p-2 bg-background border border-border rounded hover:border-accent transition-colors text-left cursor-grab active:cursor-grabbing"
                          >
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                              <svg width="14" height="14" viewBox="0 0 16 16">
                                <PaletteIcon type={comp.type} size={14} />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-foreground truncate">
                                {comp.name}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

              {filteredComponents.length === 0 && (
                <div className="text-center py-8 text-foreground-muted text-xs">
                  No components
                </div>
              )}
            </>
          )}

          {activeTab === 'diagrams' && (
            <div className="space-y-1">
              {allDiagrams
                .sort((a, b) => b.updatedAt - a.updatedAt)
                .map((diagram) => (
                  <button
                    key={diagram.id}
                    onClick={() => onLoadDiagram?.(diagram.id)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setDiagramContextMenu({
                        x: e.clientX,
                        y: e.clientY,
                        diagram,
                      });
                    }}
                    className="w-full p-2 bg-background border border-border rounded hover:border-accent transition-colors text-left"
                  >
                    <div className="text-xs font-medium text-foreground truncate">
                      {diagram.name}
                    </div>
                    <div className="text-xs text-foreground-muted mt-1">
                      {diagram.elementCount} elements · {diagram.relationshipCount} rels
                    </div>
                    <div className="text-xs text-foreground-muted mt-0.5">
                      {new Date(diagram.updatedAt).toLocaleDateString()}
                    </div>
                  </button>
                ))}

              {allDiagrams.length === 0 && (
                <div className="text-center py-8 text-foreground-muted text-xs">
                  No diagrams found
                </div>
              )}
            </div>
          )}
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={[
              {
                label: 'Inspect',
                icon: '🔍',
                onClick: () => {
                  if (onInspectComponent) {
                    onInspectComponent(contextMenu.element);
                  }
                },
              },
              {
                label: 'Rename',
                icon: '✎',
                onClick: () => {
                  setRenameElement(contextMenu.element);
                  setRenameName(contextMenu.element.name);
                },
              },
              {
                label: 'Delete from Model',
                icon: '⚠',
                onClick: () => {
                  if (onDeleteComponent) {
                    onDeleteComponent(contextMenu.element);
                  }
                },
                danger: true,
              },
            ]}
            onClose={() => setContextMenu(null)}
          />
        )}

        {/* Diagram Context Menu */}
        {diagramContextMenu && (
          <DiagramContextMenu
            x={diagramContextMenu.x}
            y={diagramContextMenu.y}
            diagram={diagramContextMenu.diagram}
            onRename={(diagram) => {
              setRenameDiagram(diagram);
              setRenameDiagramName(diagram.name);
            }}
            onDelete={(diagramId) => {
              if (onDeleteDiagram) {
                onDeleteDiagram(diagramId);
              }
            }}
            onClose={() => setDiagramContextMenu(null)}
          />
        )}

        {/* Rename Dialog */}
        {renameElement && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
            <div className="bg-background border-2 border-accent rounded-lg p-4 w-80">
              <h3 className="text-sm font-bold text-foreground mb-3">Rename Component</h3>
              <input
                type="text"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renameName.trim()) {
                    if (onRenameComponent) {
                      onRenameComponent(renameElement, renameName.trim());
                    }
                    setRenameElement(null);
                  } else if (e.key === 'Escape') {
                    setRenameElement(null);
                  }
                }}
                autoFocus
                className="w-full px-2 py-1 bg-surface border border-border rounded text-foreground text-sm mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (renameName.trim() && onRenameComponent) {
                      onRenameComponent(renameElement, renameName.trim());
                    }
                    setRenameElement(null);
                  }}
                  className="flex-1 px-2 py-1 bg-accent text-white rounded text-xs hover:bg-accent-hover"
                >
                  Save
                </button>
                <button
                  onClick={() => setRenameElement(null)}
                  className="px-2 py-1 bg-surface text-foreground rounded text-xs hover:bg-accent-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rename Diagram Dialog */}
        {renameDiagram && (
          <RenameDiagramDialog
            diagram={renameDiagram}
            value={renameDiagramName}
            onChange={setRenameDiagramName}
            onSave={(diagramId, newName) => {
              if (onRenameDiagram) {
                onRenameDiagram(diagramId, newName);
              }
              setRenameDiagram(null);
            }}
            onCancel={() => setRenameDiagram(null)}
            compact={true}
          />
        )}
      </div>
    );
  }

  // Modal mode: centered dialog
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border-2 border-accent rounded-lg w-full max-w-5xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-border p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-foreground">Library</h2>
            <div className="flex gap-3">
              {onToggleDock && (
                <button
                  onClick={onToggleDock}
                  className="text-foreground-tertiary hover:text-foreground"
                  title="Dock to left"
                >
                  ⇲ Dock
                </button>
              )}
              <button onClick={onClose} className="text-foreground-tertiary hover:text-foreground text-xl">✕</button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveTab('components')}
              className={`px-4 py-2 rounded transition-colors ${
                activeTab === 'components'
                  ? 'bg-accent text-white'
                  : 'bg-surface text-foreground-secondary hover:bg-accent-muted'
              }`}
            >
              Components ({filteredComponents.length})
            </button>
            <button
              onClick={() => setActiveTab('diagrams')}
              className={`px-4 py-2 rounded transition-colors ${
                activeTab === 'diagrams'
                  ? 'bg-accent text-white'
                  : 'bg-surface text-foreground-secondary hover:bg-accent-muted'
              }`}
            >
              Diagrams ({allDiagrams.length})
            </button>
          </div>

          {activeTab === 'components' && (
            <>
              {/* Search and filters */}
              <div className="flex gap-4">
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search components..."
                  className="flex-1 px-3 py-2 bg-surface border border-border rounded text-foreground"
                />
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as 'type' | 'layer')}
                  className="px-3 py-2 bg-surface border border-border rounded text-foreground"
                >
                  <option value="layer">Group by Layer</option>
                  <option value="type">Group by Type</option>
                </select>
              </div>

              <div className="mt-2 text-sm text-foreground-tertiary">
                {filteredComponents.length} components across all diagrams
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'components' && (
            <>
              {Object.entries(grouped)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([group, components]) => (
                  <div key={group} className="mb-6">
                    <h3 className="text-sm font-semibold text-accent mb-3 uppercase tracking-wide">
                      {group} ({components.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {components.map((comp) => {
                        const metadata = getElementMetadata(comp.type);
                        if (!metadata) return null;

                        return (
                          <button
                            key={comp.id}
                            onClick={() => {
                              onSelectComponent(comp);
                              onClose();
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setContextMenu({
                                x: e.clientX,
                                y: e.clientY,
                                element: comp,
                              });
                            }}
                            className="flex items-center gap-3 p-3 bg-surface border border-border rounded hover:border-accent transition-colors text-left"
                            style={{ backgroundColor: `${metadata.color}20` }}
                          >
                            <div className="w-8 h-8 flex items-center justify-center">
                              <svg width="16" height="16" viewBox="0 0 16 16">
                                <PaletteIcon type={comp.type} size={16} />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">
                                {comp.name}
                              </div>
                              <div className="text-xs text-foreground-tertiary capitalize">
                                {metadata.name}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

              {filteredComponents.length === 0 && (
                <div className="text-center py-12 text-foreground-muted">
                  No components found.
                  <br />
                  Create elements in your diagrams to see them here.
                </div>
              )}
            </>
          )}

          {activeTab === 'diagrams' && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {allDiagrams
                .sort((a, b) => b.updatedAt - a.updatedAt)
                .map((diagram) => (
                  <button
                    key={diagram.id}
                    onClick={() => {
                      onLoadDiagram?.(diagram.id);
                      onClose();
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setDiagramContextMenu({
                        x: e.clientX,
                        y: e.clientY,
                        diagram,
                      });
                    }}
                    className="p-4 bg-surface border border-border rounded hover:border-accent transition-colors text-left"
                  >
                    <div className="text-sm font-medium text-foreground truncate mb-2">
                      {diagram.name}
                    </div>
                    <div className="text-xs text-foreground-muted">
                      {diagram.elementCount} elements
                    </div>
                    <div className="text-xs text-foreground-muted">
                      {diagram.relationshipCount} relationships
                    </div>
                    <div className="text-xs text-foreground-muted mt-2">
                      {new Date(diagram.updatedAt).toLocaleDateString()}
                    </div>
                  </button>
                ))}

              {allDiagrams.length === 0 && (
                <div className="col-span-full text-center py-12 text-foreground-muted">
                  No diagrams found.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={[
              {
                label: 'Inspect',
                icon: '🔍',
                onClick: () => {
                  if (onInspectComponent) {
                    onInspectComponent(contextMenu.element);
                  }
                },
              },
              {
                label: 'Rename',
                icon: '✎',
                onClick: () => {
                  setRenameElement(contextMenu.element);
                  setRenameName(contextMenu.element.name);
                },
              },
              {
                label: 'Delete from Model',
                icon: '⚠',
                onClick: () => {
                  if (onDeleteComponent) {
                    onDeleteComponent(contextMenu.element);
                  }
                },
                danger: true,
              },
            ]}
            onClose={() => setContextMenu(null)}
          />
        )}

        {/* Diagram Context Menu */}
        {diagramContextMenu && (
          <DiagramContextMenu
            x={diagramContextMenu.x}
            y={diagramContextMenu.y}
            diagram={diagramContextMenu.diagram}
            onRename={(diagram) => {
              setRenameDiagram(diagram);
              setRenameDiagramName(diagram.name);
            }}
            onDelete={(diagramId) => {
              if (onDeleteDiagram) {
                onDeleteDiagram(diagramId);
              }
            }}
            onClose={() => setDiagramContextMenu(null)}
          />
        )}

        {/* Rename Dialog */}
        {renameElement && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
            <div className="bg-background border-2 border-accent rounded-lg p-6">
              <h3 className="text-lg font-bold text-foreground mb-4">Rename Component</h3>
              <input
                type="text"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renameName.trim()) {
                    if (onRenameComponent) {
                      onRenameComponent(renameElement, renameName.trim());
                    }
                    setRenameElement(null);
                  } else if (e.key === 'Escape') {
                    setRenameElement(null);
                  }
                }}
                autoFocus
                className="w-full px-3 py-2 bg-surface border border-border rounded text-foreground mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (renameName.trim() && onRenameComponent) {
                      onRenameComponent(renameElement, renameName.trim());
                    }
                    setRenameElement(null);
                  }}
                  className="flex-1 px-4 py-2 bg-accent text-white rounded hover:bg-accent-hover"
                >
                  Save
                </button>
                <button
                  onClick={() => setRenameElement(null)}
                  className="px-4 py-2 bg-surface text-foreground rounded hover:bg-accent-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rename Diagram Dialog */}
        {renameDiagram && (
          <RenameDiagramDialog
            diagram={renameDiagram}
            value={renameDiagramName}
            onChange={setRenameDiagramName}
            onSave={(diagramId, newName) => {
              if (onRenameDiagram) {
                onRenameDiagram(diagramId, newName);
              }
              setRenameDiagram(null);
            }}
            onCancel={() => setRenameDiagram(null)}
            compact={false}
          />
        )}
      </div>
    </div>
  );
}
